import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutGrid, Settings, Plus, FileDown, User, LogOut, CreditCard, Users, Activity, AlertTriangle,
  ShieldCheck, BookOpen, Building2, SlidersHorizontal, Mail, Eye, Gift, Bot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { handbookPendingCount, adminUnansweredCount } from "@/lib/api";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ConsentBanner from "@/components/ConsentBanner";
import TrialBanner from "@/components/TrialBanner";
import logo from "@/assets/logo.png";

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [hbPending, setHbPending] = useState(0);
  const [walterOpen, setWalterOpen] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    handbookPendingCount()
      .then((d) => setHbPending(d.count || 0))
      .catch(() => {});
    adminUnansweredCount()
      .then((c) => setWalterOpen(c || 0))
      .catch(() => {});
  }, [isAdmin, location.pathname]);

  // After the trial-reminder email was sent, highlight the "Abo" menu item in red
  // with the remaining hours until the free trial ends.
  const showAboAlert =
    !!user?.trial_reminder_sent && !user?.exempt && user?.subscription_status === "trial";
  const aboHoursLeft =
    showAboAlert && user?.trial_ends_at
      ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 3600000))
      : 0;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const at = user?.account_type;
  const canSeeMandanten = at === "steuerberater" || at === "kanzlei_mitarbeiter";
  const canSeeMitarbeiter =
    at === "steuerberater" || at === "kanzlei_mitarbeiter" || at === "unternehmer";
  // Mitarbeiter mit reiner Einsichts-Berechtigung (view_others, ohne Verwaltungsrechte):
  // dürfen andere Konten nur ansehen → eigener Menüpunkt „Konten".
  const canSeeKonten = at === "mitarbeiter" && (user?.permissions || []).includes("view_others");

  const nav = [
    { to: "/", label: "Belege", icon: LayoutGrid, key: "belege" },
    { to: "/export", label: "Export", icon: FileDown, key: "export" },
    ...(canSeeMandanten
      ? [{ to: "/organisation?ansicht=mandanten", label: "Mandanten", icon: Building2, key: "mandanten" }]
      : []),
    ...(canSeeMitarbeiter
      ? [{ to: "/organisation?ansicht=mitarbeiter", label: "Mitarbeiter", icon: Users, key: "mitarbeiter" }]
      : []),
    ...(canSeeKonten
      ? [{ to: "/organisation", label: "Konten", icon: Eye, key: "konten" }]
      : []),
  ];

  const ansicht = new URLSearchParams(location.search).get("ansicht");
  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
  const navActive = (n) => {
    if (n.key === "mandanten")
      return location.pathname.startsWith("/organisation") && ansicht === "mandanten";
    if (n.key === "mitarbeiter")
      return location.pathname.startsWith("/organisation") && ansicht === "mitarbeiter";
    return isActive(n.to.split("?")[0]);
  };

  // Einstellungen-Submenü (Dropdown): Einstellungen(Stammdaten), Benutzer, Abo, Handbuch, Logout.
  const settingsRoutes = ["/stammdaten", "/benutzer", "/abo", "/handbuch"];
  const settingsActive = settingsRoutes.some((r) => location.pathname.startsWith(r));

  return (
    <div className="min-h-screen bg-background">
      <TrialBanner />
      <ImpersonationBanner />
      <ConsentBanner />
      {user?.topup_blocked && (
        <div className="bg-red-600 text-white" data-testid="topup-blocked-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-medium min-w-0">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {user?.requires_topup
                  ? "Guthaben aufgebraucht – vorhandene Belege bleiben verfügbar, neue Belege/Konten sind gesperrt."
                  : "Guthaben Ihres Verwalters aufgebraucht – vorhandene Belege bleiben verfügbar, neue Belege sind gesperrt."}
              </span>
            </div>
            {user?.requires_topup && (
              <Link
                to="/abo"
                data-testid="topup-banner-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-md bg-white/15 hover:bg-white/25 px-3 py-1.5 text-sm font-semibold transition-colors"
              >
                <CreditCard className="h-4 w-4" /> Jetzt aufladen
              </Link>
            )}
          </div>
        </div>
      )}
      <header className="border-b border-border bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-[30px] flex items-center justify-center md:justify-between">
          <div className="flex items-center gap-3 w-1/2 md:w-auto justify-center md:justify-start">
            <Link
              to={isAdmin ? "/administration" : "/"}
              className="flex items-center"
              data-testid="brand-logo"
            >
              <img
                src={logo}
                alt="Besca"
                className="block w-full h-auto md:w-auto md:h-[50px]"
              />
            </Link>
            {(user?.name || user?.company) && (
              <span
                className="hidden md:inline-flex items-center text-sm font-medium text-muted-foreground border-l border-border pl-3 max-w-[320px] truncate"
                data-testid="header-company"
                title={[user?.name, user?.company].filter(Boolean).join(" | ")}
              >
                {[user?.name, user?.company].filter(Boolean).join(" | ")}
              </span>
            )}
          </div>

          {isAdmin ? (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/administration"
                data-testid="admin-nav-administration"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${
                  isActive("/administration")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <ShieldCheck className="h-4 w-4" />
                <span>Administration</span>
              </Link>
              <Link
                to="/aktivitaet"
                data-testid="admin-nav-activity"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${
                  isActive("/aktivitaet")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <Activity className="h-4 w-4" />
                <span>Aktivität</span>
              </Link>
              <Link
                to="/mails"
                data-testid="admin-nav-mails"
                className={`relative px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${
                  isActive("/mails")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <Mail className="h-4 w-4" />
                <span>Mails</span>
              </Link>
              <Link
                to="/handbuch"
                data-testid="admin-nav-handbuch"
                className={`relative px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${
                  isActive("/handbuch")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <BookOpen className="h-4 w-4" />
                <span>Handbuch</span>
                {hbPending > 0 && (
                  <span
                    className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] h-4 min-w-4 px-1"
                    data-testid="admin-nav-handbuch-badge"
                  >
                    {hbPending}
                  </span>
                )}
              </Link>
              <Link
                to="/mein-walter"
                data-testid="admin-nav-mein-walter"
                className={`relative px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${
                  isActive("/mein-walter")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <Bot className="h-4 w-4" />
                <span>Mein Walter</span>
                {walterOpen > 0 && (
                  <span
                    className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] h-4 min-w-4 px-1"
                    data-testid="admin-nav-walter-badge"
                  >
                    {walterOpen}
                  </span>
                )}
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="ml-1 p-2 rounded-md transition-colors duration-150 flex items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                data-testid="admin-logout"
                title="Abmelden"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((n) => {
                const Icon = n.icon;
                const active = navActive(n);
                const stateClass = active
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted";
                return (
                  <Link
                    key={n.key}
                    to={n.to}
                    data-testid={`nav-${n.key}`}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${stateClass}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{n.label}</span>
                  </Link>
                );
              })}
              <Button
                data-testid="nav-new-receipt"
                onClick={() => navigate("/neu")}
                disabled={user?.topup_blocked}
                title={user?.topup_blocked ? "Guthaben aufgebraucht – Aufladung erforderlich" : "Neuer Beleg"}
                className="ml-2 gap-2"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Neuer Beleg</span>
              </Button>

              {user?.account_type === "einzelnutzer" && (
                <Link
                  to="/empfehlungen"
                  data-testid="nav-referrals"
                  title="Weiterempfehlen & LifeTime gratis"
                  className={`ml-1 p-2 rounded-md transition-colors duration-150 flex items-center ${
                    location.pathname === "/empfehlungen"
                      ? "bg-red-50 text-red-600"
                      : "text-red-600 hover:bg-red-50"
                  }`}
                >
                  <Gift className="h-5 w-5" />
                </Link>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="nav-einstellungen"
                    title="Einstellungen"
                    className={`ml-1 p-2 rounded-md transition-colors duration-150 flex items-center ${
                      settingsActive || showAboAlert
                        ? showAboAlert
                          ? "text-destructive hover:bg-destructive/10"
                          : "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <Settings className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem asChild data-testid="menu-stammdaten">
                    <Link to="/stammdaten" className="cursor-pointer">
                      <SlidersHorizontal className="h-4 w-4 mr-2" /> Einstellungen
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-benutzer">
                    <Link to="/benutzer" className="cursor-pointer">
                      <User className="h-4 w-4 mr-2" /> Benutzer
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-abo">
                    <Link
                      to="/abo"
                      className={`cursor-pointer ${showAboAlert ? "text-destructive font-medium" : ""}`}
                    >
                      <CreditCard className="h-4 w-4 mr-2" /> Abo
                      {showAboAlert && aboHoursLeft > 0 && (
                        <span className="ml-auto text-xs">endet in {aboHoursLeft}h</span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-handbuch">
                    <Link to="/handbuch" className="cursor-pointer">
                      <BookOpen className="h-4 w-4 mr-2" /> Handbuch
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    data-testid="menu-logout"
                    className="cursor-pointer text-destructive focus:!bg-destructive focus:!text-white data-[highlighted]:!bg-destructive data-[highlighted]:!text-white"
                  >
                    <LogOut className="h-4 w-4 mr-2" /> Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
    </div>
  );
}
